import React from "react";
import { useGetAdminImpactMapForCardQuery } from "../api/respectCardsApi";

/**
 * Phase 2b cards-launch — flow-style impact map for a single card.
 */
interface CardImpactPanelProps {
  slug: string;
}

const wrapper: React.CSSProperties = {
  border: "1px solid #ddd6f3",
  borderRadius: 8,
  padding: 12,
  background: "#fbfaff",
  marginTop: 16,
};

const flowLine: React.CSSProperties = {
  fontFamily: "monospace",
  fontSize: "0.82rem",
  color: "#333",
  whiteSpace: "pre-wrap",
  marginBottom: 4,
};

const cimpMobileStyles = (
  <style>{`
    @media (max-width: 700px) {
      .cimp-wrapper {
        padding: 8px !important;
        margin-top: 12px !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
      }
      .cimp-table-scroll {
        overflow-x: auto !important;
        -webkit-overflow-scrolling: touch !important;
        max-width: 100% !important;
      }
      .cimp-table-scroll table {
        min-width: 240px !important;
      }
    }
  `}</style>
);

const CardImpactPanel: React.FC<CardImpactPanelProps> = ({ slug }) => {
  const skip = !slug || !slug.trim();
  const { data, isLoading, error } = useGetAdminImpactMapForCardQuery(slug, {
    skip,
  });

  if (skip) {
    return (
      <>
        {cimpMobileStyles}
      <div className="cimp-wrapper" style={wrapper}>
        <strong style={{ fontSize: "0.95rem", color: "#333" }}>Impact map</strong>
        <p style={{ fontSize: "0.82rem", color: "#666", marginTop: 8 }}>
          enter a card slug above to see its impact map.
        </p>
      </div>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        {cimpMobileStyles}
      <div className="cimp-wrapper" style={wrapper}>
        <strong style={{ fontSize: "0.95rem", color: "#333" }}>Impact map</strong>
        <p style={{ fontSize: "0.82rem", color: "#666", marginTop: 8 }}>
          loading impact map for <code>{slug}</code>…
        </p>
      </div>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        {cimpMobileStyles}
      <div className="cimp-wrapper" style={wrapper}>
        <strong style={{ fontSize: "0.95rem", color: "#333" }}>Impact map</strong>
        <p style={{ fontSize: "0.82rem", color: "#a00", marginTop: 8 }}>
          no impact doc for <code>{slug}</code>. add one in{" "}
          <code>cardImpactRegistry</code>.
        </p>
      </div>
      </>
    );
  }

  const { impact } = data;
  const hook = impact.hooksTouched[0] ?? "—";
  const route = impact.routesTouched[0] ?? "—";
  const cancelable =
    impact.cancelableBy && impact.cancelableBy.length > 0
      ? impact.cancelableBy.join(", ")
      : "natural expiry";

  return (
    <>
      {cimpMobileStyles}
    <div className="cimp-wrapper" style={wrapper}>
      <strong style={{ fontSize: "0.95rem", color: "#333" }}>Impact map</strong>
      <p style={{ fontSize: "0.78rem", color: "#666", margin: "4px 0 12px" }}>
        effect type: <code>{data.effectType}</code> — visible to:{" "}
        <code>{impact.visibleTo}</code>
      </p>
      <div>
        <div style={flowLine}>[Card played]</div>
        <div style={flowLine}>{`   └──> Inserts ActiveEffect (scope: ${
          impact.whenItRuns || "—"
        })`}</div>
        <div style={flowLine}>{`            └──> Hook '${hook}' on ${route}`}</div>
        <div style={flowLine}>{`                     └──> ${impact.oneLine}`}</div>
        <div style={flowLine}>{`                     └──> Cancelable by: ${cancelable}`}</div>
      </div>
      {impact.paramsExplained && impact.paramsExplained.length > 0 ? (
        <div className="cimp-table-scroll">
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.8rem",
            marginTop: 12,
          }}
        >
          <thead>
            <tr style={{ background: "#f5f5fa", textAlign: "left" }}>
              <th style={{ padding: 6 }}>key</th>
              <th style={{ padding: 6 }}>meaning</th>
            </tr>
          </thead>
          <tbody>
            {impact.paramsExplained.map((p) => (
              <tr key={p.key} style={{ borderTop: "1px solid #eee" }}>
                <td style={{ padding: 6 }}>
                  <code>{p.key}</code>
                </td>
                <td style={{ padding: 6 }}>{p.meaning}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      ) : null}
      {impact.notes ? (
        <p style={{ fontSize: "0.78rem", color: "#666", marginTop: 8 }}>
          {impact.notes}
        </p>
      ) : null}
    </div>
    </>
  );
};

export default CardImpactPanel;
